import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Search } from 'lucide-react';
import { ReviewFeedbackResource } from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';

/**
 * Feedback negatif dari prompt "review us" di aplikasi POS. Sampai halaman
 * ini ada, keluhan yang diketik merchant hanya mengendap di tabel
 * `company_review_feedbacks` tanpa pernah dibaca siapa pun — endpoint
 * bacanya `GET /admin/review-feedbacks` (kloa-pos).
 */
const ReviewFeedbacks: React.FC = () => {
  const [items, setItems] = useState<ReviewFeedbackResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.getReviewFeedbacks({
        page,
        perPage,
        search: search || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      });
      setItems(res.data);
      setTotal(res.meta.total);
    } catch (e) {
      console.error('Error loading review feedbacks:', e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const formatWaktu = (iso: string | null) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('id-ID', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <MessageSquare className="h-7 w-7 text-red-500" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Review Feedback</h1>
          <p className="text-sm text-gray-500">
            Keluhan merchant dari prompt "review us" di aplikasi — masukan paling jujur, dari yang hampir memberi bintang jelek.
          </p>
        </div>
      </div>

      <form onSubmit={submitSearch} className="bg-white rounded-lg shadow p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Cari perusahaan</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Nama perusahaan..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Dari tanggal</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-md text-sm px-3 py-2"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Sampai tanggal</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
            className="border border-gray-300 rounded-md text-sm px-3 py-2"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700"
        >
          Cari
        </button>
      </form>

      {loading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-10 text-center text-gray-500 text-sm">
          Tidak ada feedback pada filter ini.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((fb) => (
            <div key={fb.id} className="bg-white rounded-lg shadow p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-gray-900">{fb.companyName ?? '(perusahaan terhapus)'}</span>
                {fb.category && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{fb.category}</span>
                )}
                {fb.threshold != null && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    ~{fb.threshold} transaksi
                  </span>
                )}
                <span className="ml-auto text-xs text-gray-400">{formatWaktu(fb.createdAt)}</span>
              </div>
              {fb.note ? (
                <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{fb.note}</p>
              ) : (
                <p className="mt-2 text-sm text-gray-400 italic">(tanpa catatan — hanya memilih kategori)</p>
              )}
              {fb.userName && (
                <p className="mt-2 text-xs text-gray-400">Ditulis oleh: {fb.userName}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {!loading && total > perPage && (
        <Pagination currentPage={page} total={total} perPage={perPage} onPageChange={setPage} />
      )}
    </div>
  );
};

export default ReviewFeedbacks;
